import React, { useEffect, useState } from 'react';
import styles from './style.module.scss';
import classNames from 'classnames';
import DashboardWrapper from 'shared/components/common/dashboard_wrapper/DashboardWrapper';
import Heading from 'shared/components/common/heading/Heading';
import CustomButton from 'shared/components/common/custom_button/CustomButton';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { routeConstant } from 'shared/routes/RouteConstant';
import { toastMessage } from 'shared/components/toast/Toast';
import OverlayLoading from 'shared/screen_loader/overlay_loader/OverlayLoading';
import { GetAllWarehouse } from 'shared/services/ware_house_service/WareHouseService';
import { GetSkuByWarehouseQuery } from 'shared/services/inventory_service/InventoryService';
import { AddITMutation } from 'shared/services/it_service/ITService';
import InnerSoTable from '../sale_order/InnerSoTable'; // We can reuse or create a specialized one.
import InternalTransferModal from 'shared/modal/internal_transfer/InternalTransferModal';

export default function AddInternalTransfer() {
    const navigate = useNavigate();
    const { theme: { theme } } = useSelector((state) => state.root);
    const isDark = theme?.toLowerCase() === 'dark';

    const { data: listWareHouse } = GetAllWarehouse();
    const { mutate: addITMutation, isLoading: ITLoading, isSuccess: ITIsSuccess } = AddITMutation();

    const [wareHouses, setWareHouses] = useState([]);
    const [sourceWarehouse, setSourceWarehouse] = useState(null);
    const [destWarehouse, setDestWarehouse] = useState(null);
    const [itListing, setItListing] = useState([]);
    const [showModal, setShowModal] = useState(false);

    const { data: skuData } = GetSkuByWarehouseQuery(sourceWarehouse?.value);

    const skuOptions = skuData?.map(item => ({
        label: `${item?.sku} (Stock: ${item?.quantity}, Price: ${item?.price || 0})`,
        value: item?.sku,
        fullData: item
    })) || [];

    useEffect(() => {
        if (listWareHouse) {
            const options = listWareHouse.map(w => ({
                label: w?.code,
                value: w?._id,
            }));
            setWareHouses(options);
        }
    }, [listWareHouse]);

    useEffect(() => {
        if (ITIsSuccess) {
            navigate(routeConstant.admin.internalTransfer.list.path);
        }
    }, [ITIsSuccess, navigate]);

    const handleSkuSelect = (selected) => {
        const data = selected?.fullData;
        if (!data) return;

        const isDuplicate = itListing.some(item => item.sku === data.sku);
        if (isDuplicate) {
            toastMessage('warning', "This SKU is already in the list");
            return;
        }

        const newItem = {
            sku: data.sku,
            product: data.product,
            brand: data.brand,
            competebleBrand: data.compatiableBrand,
            product_category: data.product_category,
            sub_category: data.sub_category,
            quantity: data.quantity || 1, // Default to actual quantity (stock)
            price: data.price || 0,
            color: data.color || "N/A",
            raw_images: data.raw_images || [],
            maxQuantity: data.quantity // Track available stock
        };

        setItListing(prev => [...prev, newItem]);
        toastMessage('success', `SKU ${data.sku} added`);
    };

    const handleUpdateQuantity = (index, newQty) => {
        const updatedList = [...itListing];
        if (updatedList[index]) {
            const quantity = Number(newQty);
            const max = Number(updatedList[index].maxQuantity);

            if (quantity > max) {
                toastMessage('error', `Source warehouse only has ${max} units available.`);
                return;
            }

            updatedList[index].quantity = quantity;
            setItListing(updatedList);
            toastMessage('success', "Quantity updated successfully");
        }
    };

    const handleDeleteItem = (indexToDelete) => {
        setItListing(prev => prev.filter((_, index) => index !== indexToDelete));
    };

    const handleSubmit = () => {
        if (!sourceWarehouse) return toastMessage('error', "Please select source warehouse");
        if (!destWarehouse) return toastMessage('error', "Please select destination warehouse");
        if (sourceWarehouse.value === destWarehouse.value) return toastMessage('error', "Source and Destination cannot be same");
        if (itListing.length === 0) return toastMessage('error', "Please add at least one item");

        setShowModal(true);
    };

    const handleFinalSubmit = (modalValues) => {
        const payload = {
            fromWarehouse: sourceWarehouse.value,
            toWarehouse: destWarehouse.value,
            items: itListing.map(item => ({
                sku: item.sku,
                quantity: item.quantity
            })),
            transferPerson: modalValues.personName,
            referenceNumber: modalValues.refNumber,
        };

        addITMutation(payload);
    };

    const customSelectStyles = {
        control: (provided, state) => ({
            ...provided,
            borderRadius: '8px',
            border: state.isFocused ? '1px solid #4a86f7' : '1px solid #c9d1e1',
            boxShadow: state.isFocused ? '0 0 0 1px #4a86f7' : 'none',
            '&:hover': { border: '1px solid #4a86f7' },
            padding: '2px 4px',
            minHeight: '45px',
            backgroundColor: isDark ? '#262c48' : '#fff',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
        }),
        placeholder: (provided) => ({ ...provided, color: '#a8a8a8', fontSize: '14px' }),
        singleValue: (provided) => ({ ...provided, color: isDark ? '#fff' : '#000' }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: isDark ? '#262c48' : '#fff',
            borderRadius: '8px',
            overflow: 'hidden',
            zIndex: 100,
            border: isDark ? '1px solid #353f5f' : '1px solid #c9d1e1',
            boxShadow: '0 4px 11px rgba(0, 0, 0, 0.15)',
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#4a86f7' : state.isFocused ? (isDark ? '#353f5f' : '#e2ecff') : 'transparent',
            color: state.isSelected ? '#fff' : (isDark ? '#fff' : '#000'),
            cursor: 'pointer',
            padding: '10px 15px',
        }),
        input: (provided) => ({ ...provided, color: isDark ? '#fff' : '#000' }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    };

    return (
        <DashboardWrapper>
            <div className={classNames(styles.UserContainer, 'text-start')}>
                <div className={styles.Header}>
                    <Heading heading={'Add Internal Transfer'} headingStyle={styles.Heading} />
                    <div className={styles.SelectionRow}>
                        <div className={styles.SelectContainer}>
                            <label className={styles.InputLabel}>Source Warehouse</label>
                            <Select
                                options={wareHouses}
                                styles={customSelectStyles}
                                isSearchable
                                placeholder="Select Source Warehouse"
                                menuPortalTarget={document.body}
                                onChange={(selected) => {
                                    setSourceWarehouse(selected);
                                    setItListing([]); // Clear items if warehouse changes
                                }}
                                value={sourceWarehouse}
                            />
                        </div>
                        <div className={styles.SelectContainer}>
                            <label className={styles.InputLabel}>Search SKU</label>
                            <Select
                                options={skuOptions}
                                styles={customSelectStyles}
                                isSearchable
                                placeholder="Search SKU to Add"
                                menuPortalTarget={document.body}
                                onChange={handleSkuSelect}
                                value={null}
                                isDisabled={!sourceWarehouse}
                            />
                        </div>
                        <div className={styles.SelectContainer}>
                            <label className={styles.InputLabel}>Destination Warehouse</label>
                            <Select
                                options={wareHouses.filter(w => w.value !== sourceWarehouse?.value)}
                                styles={customSelectStyles}
                                isSearchable
                                placeholder="Select Destination Warehouse"
                                menuPortalTarget={document.body}
                                onChange={(selected) => setDestWarehouse(selected)}
                                value={destWarehouse}
                            />
                        </div>
                    </div>
                </div>

                <div className={classNames(styles.Body, "mt-4")}>
                    <InnerSoTable
                        PoList={itListing}
                        handleDelete={handleDeleteItem}
                        handleUpdateQuantity={handleUpdateQuantity}
                        title="Items to Transfer"
                    />

                    {itListing.length > 0 && (
                        <div className={classNames(styles.ButtonContainer, 'd-flex justify-content-end mt-3')}>
                            <CustomButton
                                text={'Submit Transfer'}
                                containerStyle={styles.SubmitBtn}
                                submitHandle={handleSubmit}
                            />
                        </div>
                    )}
                </div>
                <InternalTransferModal
                    show={showModal}
                    handleClose={() => setShowModal(false)}
                    onSubmit={handleFinalSubmit}
                />
            </div>
            {ITLoading && <OverlayLoading />}
        </DashboardWrapper>
    );
}
